import type { ZxcvbnResult } from '@zxcvbn-ts/core';

export type PasswordStrengthResult = {
  score: ZxcvbnResult['score'];
  feedback: ZxcvbnResult['feedback'];
};

type CheckFn = (password: string, userInputs?: (string | number)[]) => ZxcvbnResult;

// The language dictionaries are ~230KB gzipped, so they're dynamically imported
// (not in the main bundle) and the built factory is memoized. Keep this the only
// module that imports `@zxcvbn-ts/*` so that stays true.
let scorerPromise: Promise<CheckFn> | null = null;

async function loadScorer(): Promise<CheckFn> {
  const [{ ZxcvbnFactory }, common, en] = await Promise.all([
    import('@zxcvbn-ts/core'),
    import('@zxcvbn-ts/language-common'),
    import('@zxcvbn-ts/language-en'),
  ]);

  const zxcvbn = new ZxcvbnFactory({
    dictionary: {
      ...common.dictionary,
      ...en.dictionary,
    },
    graphs: common.adjacencyGraphs,
    translations: en.translations,
  });

  return (password, userInputs) => zxcvbn.check(password, userInputs);
}

/**
 * Scores a password from 0 (weakest) to 4 (strongest) with zxcvbn-ts.
 *
 * @param userInputs - Site/user-specific strings (email, name, workspace, …)
 *   that should be penalized if they appear in the password.
 */
export async function scorePassword(
  password: string,
  userInputs?: (string | number)[],
): Promise<PasswordStrengthResult> {
  scorerPromise ??= loadScorer().catch((error: unknown) => {
    // Reset so a later call can retry the import (e.g. a transient chunk 404).
    scorerPromise = null;
    throw error;
  });

  const check = await scorerPromise;
  const { score, feedback } = check(password, userInputs);
  return { score, feedback };
}
